import { Center, Flex, Box } from '@chakra-ui/react';
import { useField } from 'formik';
import { KText } from '@/ui-library/components/text';
import { useIsMobile } from '@/hooks/use-IsMobile';

export const RatingField = ({ item }) => {
  const {
    name,
    count = 10,
    min = 0,
    max,
    disabled,
    editable = true,
    isNPS = false,
    label,
    isRequired = false,
    subLabel,
  } = item;

  const isMobile = useIsMobile();

  // Use min and max if provided, otherwise use count with default starting from 0
  const startValue = min;
  const endValue = max !== undefined ? max : startValue + count - 1;
  const rangeLength = endValue - startValue + 1;

  const ranges = Array.from(
    { length: rangeLength },
    (_, index) => startValue + index
  );

  // eslint-disable-next-line no-unused-vars
  const [formField, meta, helpers = {}] = useField(name);
  const { setValue } = helpers || {};
  const { value } = meta;

  const handleClick = (number) => {
    setValue(number);
  };

  // Get background color for NPS ranges
  const getNPSBackgroundColor = (range) => {
    if (range >= 0 && range <= 6) {
      return 'pastel-1-light'; // Light red/orange for detractors
    }
    if (range >= 7 && range <= 8) {
      return 'pastel-6-light'; // Light yellow for passives
    }
    if (range >= 9 && range <= 10) {
      return 'pastel-2-light'; // Light green for promoters
    }
    return 'unset';
  };

  const getSelectedNPSBackgroundColor = (range) => {
    if (range >= 0 && range <= 6) {
      return 'error-100';
    }
    if (range >= 7 && range <= 8) {
      return 'status-3';
    }
    if (range >= 9 && range <= 10) {
      return 'success-200';
    }
    return 'ats-primary';
  };

  // Get text color for NPS ranges
  const getNPSTextColor = (range) => {
    if (range >= 0 && range <= 6) {
      return 'pastel-1-dark'; // Dark red for detractors
    }
    if (range >= 7 && range <= 8) {
      return 'pastel-6-dark'; // Dark yellow for passives
    }
    if (range >= 9 && range <= 10) {
      return 'pastel-2-dark'; // Dark green for promoters
    }
    return 'secondary-700';
  };

  const content = (
    <Flex
      width="100%"
      flexDir="column"
      alignItems={isNPS ? 'center' : 'flex-start'}
      gap="12px"
    >
      {isNPS && label && (
        <KText size="md-medium" color="secondary-900" textAlign="center">
          {label}
          {isRequired && (
            <KText as="span" color="error-200" ml="4px">
              *
            </KText>
          )}
        </KText>
      )}
      {isNPS && subLabel && (
        <KText
          size="xs-regular"
          color="secondary-600"
          textAlign="center"
          mb="4px"
        >
          {subLabel}
        </KText>
      )}
      <Flex
        flexDir={isMobile ? 'column' : 'row'}
        gap={isMobile ? '12px' : '6px'}
        pointerEvents={!editable ? 'none' : 'auto'}
        alignItems={isMobile ? 'center' : isNPS ? 'center' : 'flex-start'}
        justifyContent={isMobile ? 'center' : isNPS ? 'center' : 'flex-start'}
        flexWrap={isMobile ? 'nowrap' : 'wrap'}
        maxW={isMobile ? '100%' : 'fit-content'}
        width="100%"
      >
        {isMobile
          ? // Mobile: Split ranges into rows (0-5, 6-10)
            [ranges.slice(0, 6), ranges.slice(6)].map(
              (rowRanges, rowIndex) =>
                rowRanges.length > 0 && (
                  <Flex key={rowIndex} gap="12px" justifyContent="center">
                    {rowRanges.map((range, index) => (
                      <Center
                        border={isNPS ? 'none' : '1px solid'}
                        borderColor={isNPS ? 'transparent' : 'border-color'}
                        borderRadius="4px"
                        w="36px"
                        h="36px"
                        key={index}
                        bg={
                          value === range
                            ? isNPS
                              ? getSelectedNPSBackgroundColor(range)
                              : 'ats-primary'
                            : isNPS
                            ? getNPSBackgroundColor(range)
                            : 'unset'
                        }
                        onClick={() => !disabled && handleClick(range)}
                        cursor={disabled ? 'not-allowed' : 'pointer'}
                        opacity={disabled ? 0.5 : 1}
                        flexShrink={0}
                      >
                        <KText
                          size="md-medium"
                          color={
                            value === range
                              ? 'base-white'
                              : isNPS
                              ? getNPSTextColor(range)
                              : 'secondary-500'
                          }
                        >
                          {range}
                        </KText>
                      </Center>
                    ))}
                  </Flex>
                )
            )
          : // Desktop: All ratings in single row
            ranges.map((range, index) => (
              <Center
                border={isNPS ? 'none' : '1px solid'}
                borderColor={isNPS ? 'transparent' : 'border-color'}
                borderRadius="4px"
                w="32px"
                h="32px"
                key={index}
                bg={
                  value === range
                    ? isNPS
                      ? getSelectedNPSBackgroundColor(range)
                      : 'ats-primary'
                    : isNPS
                    ? getNPSBackgroundColor(range)
                    : 'unset'
                }
                onClick={() => !disabled && handleClick(range)}
                cursor={disabled ? 'not-allowed' : 'pointer'}
                opacity={disabled ? 0.5 : 1}
                flexShrink={0}
              >
                <KText
                  size="md-medium"
                  color={
                    value === range
                      ? 'base-white'
                      : isNPS
                      ? getNPSTextColor(range)
                      : 'secondary-500'
                  }
                >
                  {range}
                </KText>
              </Center>
            ))}
      </Flex>
    </Flex>
  );

  if (isNPS) {
    return (
      <Box
        width="100%"
        borderRadius="6px"
        bg="secondary-25"
        p="24px"
        position="relative"
        sx={{
          border: 'none',
          backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='%239CB3FE' stroke-width='2' stroke-dasharray='8%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
        }}
      >
        {content}
      </Box>
    );
  }

  return content;
};
